import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { VisitStatus, type VisitState, VisitUiType } from 'store/visits/types';
import { useInfiniteScrollVisits } from 'hooks/use-infinite-scroll-visits.hook';
import { VisitorsListV2TableHead } from './visitors-list-v2-table-head';
import { VisitorsListV2TableBodyRow } from './visitors-list-v2-table-body-row';
import { Skeleton } from '@hqo/ui-atoms';
import { InfiniteScrollContainer } from '../shared/infinite-scroll-container';
import {
  reasonForVisitConfigSelector,
  floorConfigSelector,
  visitorCompanyConfigSelector,
} from 'store/ui-metadata/selectors';
import { FeatureEnablementFlagEnum } from 'store/ui-metadata/types';

const INITIAL_BATCH_SIZE = 50;

interface VisitorsListV2Props {
  visits: VisitState[];
  isLoading?: boolean;
  buildingId?: string;
  after?: string;
  before?: string;
}

const renderSkeletonRows = (count: number, showVisitSummary: boolean, showFloor: boolean) =>
  Array.from({ length: count }, (_, index) => (
    <tr key={`skeleton-${index}`} className="hover:bg-gray-50">
      <td className="px-4 py-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </td>
      <td className="px-4 py-4">
        <Skeleton className="h-4 w-40" />
      </td>
      {showVisitSummary && (
        <td className="px-4 py-4">
          <Skeleton className="h-4 w-48" />
        </td>
      )}
      <td className="px-4 py-4">
        <Skeleton className="h-4 w-36" />
      </td>
      {showFloor && (
        <td className="px-4 py-4">
          <Skeleton className="h-4 w-20" />
        </td>
      )}
      <td className="px-4 py-4">
        <Skeleton className="h-6 w-20 rounded-full" />
      </td>
      <td className="px-4 py-4 text-right">
        <Skeleton className="h-8 w-8 rounded ml-auto" />
      </td>
    </tr>
  ));

export const VisitorsListV2: FC<VisitorsListV2Props> = ({ visits, isLoading = false, buildingId, after, before }) => {
  const reasonForVisitFlag = useSelector(reasonForVisitConfigSelector);
  const showVisitSummary = reasonForVisitFlag !== FeatureEnablementFlagEnum.DISABLED;

  const floorFlag = useSelector(floorConfigSelector);
  const showFloor = floorFlag !== FeatureEnablementFlagEnum.DISABLED;

  const visitorCompanyFlag = useSelector(visitorCompanyConfigSelector);
  const showVisitorCompany = visitorCompanyFlag !== FeatureEnablementFlagEnum.DISABLED;

  const infiniteScrollState = useInfiniteScrollVisits({
    buildingId,
    type: VisitUiType.ALL,
    batchSize: INITIAL_BATCH_SIZE,
    after,
    before,
  });
  const hasLoadedVisits = visits.some((v) => v.data !== null);
  const isInitialOrRefetch = isLoading && !infiniteScrollState.isLoadingMore;

  const prevDateRangeRef = useRef({ after, before });
  const [isRefetchingAfterFilterChange, setIsRefetchingAfterFilterChange] = useState(false);

  useEffect(() => {
    if (prevDateRangeRef.current.after !== after || prevDateRangeRef.current.before !== before) {
      prevDateRangeRef.current = { after, before };
      setIsRefetchingAfterFilterChange(true);
    }
  }, [after, before]);

  useEffect(() => {
    if (!isLoading) {
      setIsRefetchingAfterFilterChange(false);
    }
  }, [isLoading]);

  const showSkeleton = isInitialOrRefetch && (!hasLoadedVisits || isRefetchingAfterFilterChange);

  return (
    <div className="h-full bg-white">
      {/* Main Content - Desktop */}
      <div className="hidden md:block">
        <div className="max-w-[1600px] mx-auto">
          <div className="min-h-[600px]">
            <div className="overflow-hidden rounded border border-gray-200 bg-white" data-testid="visits-table">
              <table className="w-full">
                <VisitorsListV2TableHead showVisitSummary={showVisitSummary} showFloor={showFloor} />
                <tbody
                  className="divide-y divide-gray-200"
                  data-testid={showSkeleton ? 'visits-table-skeleton' : undefined}
                >
                  {showSkeleton ? (
                    renderSkeletonRows(2, showVisitSummary, showFloor)
                  ) : (
                    <InfiniteScrollContainer infiniteScrollState={infiniteScrollState} variant="table">
                      {visits.map((visitData) => {
                        if (!visitData.data) {
                          return null;
                        }
                        const visit = visitData.data;

                        return (
                          <VisitorsListV2TableBodyRow
                            key={visit.id}
                            id={visit.id}
                            first_name={visit.first_name}
                            last_name={visit.last_name}
                            visitor_company_name={visit.visitors?.[0]?.company}
                            arrival_date={visit.arrival_date}
                            departure_date={visit.departure_date}
                            host={visit.host}
                            floor={visit.floor}
                            suite={visit.suite}
                            status={visit.status || visit.visit_status || VisitStatus.INVITED}
                            visit_summary={visit.description}
                            showVisitSummary={showVisitSummary}
                            showFloor={showFloor}
                            showVisitorCompany={showVisitorCompany}
                            frequency={visit.frequency}
                            vendor_company_uuid={visit.vendor_company_uuid}
                            is_group_visit={visit.is_group_visit}
                          />
                        );
                      })}
                    </InfiniteScrollContainer>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
